import { request, Paginated } from "utils/api";
import moment from "moment";
import { Club, MinimalClubInfo } from "api/clubs";
import { Court } from "./courts";
import { ReducedUserProfile } from "./user";

// participant user schema
interface BookingParticipantUser {
    firstName: string,
    lastName: string,
    profilePictureUrl: string,
}

// label tags
export interface PublicTag {
    token: string;
    attachedPictureUrl?: string;
    tagColor?: string;
}

// audience group schema
export interface AudienceGroup {
    bornAfter?: string,
    bornBefore?: string,
    clubId?: string,
    gender?: "male" | "female" | "diverse" | "none",
}

// participant schema
export interface BookingParticipant {
    checkInStatus: "check-in-required" | "not-required" | "checked-in" | "check-in-open" | "check-in-missed",
    id: string,
    invitationStatus: "pending" | "accepted" | "declined" | "requested-to-join" | "request-declined",
    isCreator: boolean,
    participantRole: "standard" | "coach",
    user: BookingParticipantUser,
    userId: string,
}
// company booking filter
export type CompanyBookingFilter = "all" | "company-shared" | "company-exclusive";

// booking types
export enum BookingType {
    Standard = "standard",
    Meetup = "meetup",
    Training = "training",
    Blocking = "blocking",
}
// booking from GET /bookings
export interface Booking {
    attachedObjects: ("sip-feedback-form")[],
    autoAcceptParticipationForGroup?: AudienceGroup,
    blockingReason: "not-blocking" | "court-blocking" | "external-blocking" | "demographic-blocking",
    bookingStatus: "pending" | "final" | "cancelled",
    bookingTags: ("competition" | "corona" | "video-conference" | "video-stream" | "commercial" | "pay-what-you-want" | "company-exclusive" | "company-shared")[],
    bookingType: BookingType,
    court: Court,
    customMaxNumParticipants?: number,
    customMinNumParticipants?: number,
    description: string,
    privateDescription: string,
    detailsDescription: string,
    endDate: string,
    finalizationDeadline?: string,
    fundsRecipientId?: string,
    groupId?: string,
    hideCreator?: boolean,
    creatorMediaOnly?: boolean,
    id: string,
    numAcceptedParticipants: number,
    numTotalParticipants: number,
    numAcceptedStandardParticipants: number,
    participants: BookingParticipant[],
    participantSpecificCheckIn: boolean,
    participationRequestType: "invite-only" | "requestable-by-followers" | "auto-accept" | "auto-accept-for-group",
    repeatEndDate: string,
    repeatType: "never" | "daily" | "weekly" | "biweekly" | "monthly",
    sportForm: string,
    standardParticipationPriceCents?: number,
    startDate: string,
    exclusiveCompanyClubId?: string,
    exclusiveCompanyPriceCents?: number,
    galleryMediaUrls?: string[],
    exclusiveCompanyClub?: MinimalClubInfo,
}

// booking rules from GET /bookings/rules
export interface BookingRules {
    availableBookingTypes: {
        allowsFinalizationDeadline: boolean,
        allowsParticipants: boolean,
        allowsParticipantSpecificCheckIn: boolean,
        allowsStandardParticipationPrice: boolean,
        availableRepeatTypes: ("never" | "daily" | "weekly" | "biweekly" | "monthly")[],
        blueprints?: ("external-blocking" | "court-blocking" | "demographic-blocking")[],
        bookingType: BookingType,
        repeated: boolean,
        requiresDescription: boolean,
        requiresParticipants: boolean,
    }[],
    bookingAtomMins: number,
    closeTimeMins: number,
    minDurationMins: number,
    openTimeMins: number,
    personalContingentMins: number,
    priceCents: number,
}

// create booking params from POST /bookings
export interface CreateBookingParams {
    audienceGroupSpecification?: AudienceGroup,
    autoAcceptParticipationForGroup?: AudienceGroup,
    blueprint?: "external-blocking" | "court-blocking" | "demographic-blocking",
    bookingTags?: Booking["bookingTags"],
    bookingType: BookingType,
    clientLatitude?: number,
    clientLongitude?: number,
    courtIds: string[],
    customMaxNumParticipants?: number,
    customMinNumParticipants?: number,
    description?: string,
    privateDescription?: string,
    detailsDescription?: string,
    durationMins: number,
    expectedPriceCents?: number,
    finalizationDeadline?: string,
    fundsRecipientId?: string,
    hideCreator?: boolean,
    creatorMediaOnly?: boolean,
    negatedAudienceGroupSpecification?: AudienceGroup,
    participants?: {
        participantRole: "standard" | "coach",
        userId: string,
    }[],
    tags?: string[],
    participationRequestType?: Booking["participationRequestType"],
    participantSpecificCheckIn?: boolean,
    repeatEndDate?: string,
    repeatType?: Booking["repeatType"],
    sportForm?: string,
    standardParticipationPriceCents?: number,
    startDate: string,
    exclusiveCompanyClubId?: string,
    exclusiveCompanyPriceCents?: number,
}

// inviteable user from GET /bookings/inviteable-users
export interface InviteableUser extends ReducedUserProfile {
    followingYou: boolean,
    isInviteableAsCoach: boolean,
}

// booking options from GET /bookings/options
export interface BookingOptions {
    possibleOptions: ("edit" | "cancel" | "answer-invitation" | "check-in" | "request-participation" | "edit-booking-groups" | "delete-participation-request" | "answer-participation-requests" | "get-session-stats" | "request-cancellation")[],
    eligibleEditingParameters: (keyof Parameters<typeof patchBooking>[0] | "bookingTags.video-stream" | "bookingTags.video-conference")[],
}

// cancellation costs from GET /bookings/cancellation-cost
export interface BookingCancellationCost {
    cancellationType: "booking-is-free" | "no-cancellation-fee" | "half-cancellation-fee",
    priceCents: number,
}

// funds recipient choices from PATCH /bookings/funds-recipients
export interface BookingFundsRecipients {
    recipientChoices: {
        displayName: string,
        id: string,
        type: "club" | "individual",
    }[],
}

// company clubs
export interface CompanyClub {
    id: string,
    clubName: string,
}

// company booking params
interface FetchBookingsParams {
    clubId?: string,
    fromDate?: string,
    filter?: CompanyBookingFilter,
    query?: string,
    isVirtualEvent?: boolean,
}

// fetch bookings in a club on a day (GET /bookings)
export const getBookings = (clubId: string, date: string) => request<Booking[]>({
    data: {
        clubId,
        day: moment(date).format("YYYY-MM-DD"),
    },
    endpoint: "/bookings",
    method: "get",
});

// fetch single booking by id (GET /bookings/single)
export const getBookingsSingle = (data: {
    bookingId: string,
}) => request<Booking>({
    data,
    endpoint: "/bookings/single",
    method: "get",
});

// create bookings (POST /bookings)
export const postBookings = (booking: CreateBookingParams) => request<Booking[]>({
    data: booking,
    endpoint: "/bookings",
    method: "post",
});

// fetch booking rules (GET /bookings/rules)
export const getBookingRules = (data: {
    courtId: string,
    startDate?: string,
    durationMins?: number,
    bookingType?: BookingType,
    ignoreOverlaps?: boolean,
}) => request<BookingRules>({
    data,
    endpoint: "/bookings/rules",
    method: "get",
});

// fetch booking options (GET /bookings/options)
export const getBookingOptions = (data: {
    bookingId: string,
}) => request<BookingOptions>({
    data,
    endpoint: "/bookings/options",
    method: "get",
});

// fetch cancellation costs (GET /bookings/cancellation-cost)
export const getBookingCancellationCost = (data: {
    bookingId: string,
}) => request<BookingCancellationCost>({
    data,
    endpoint: "/bookings/cancellation-cost",
    method: "get",
});

// fetch inviteable users (GET /bookings/inviteable-users)
export const getInviteableUsers = (data: {
    courtId?: string,
    filter?: "all" | "club" | "followers",
    inviteableAsCoachOnly?: boolean,
    inviteableOnly?: boolean,
    limit?: number,
    offset?: number,
    query?: string,
}) => request<Paginated<InviteableUser>>({
    data,
    endpoint: "/bookings/inviteable-users",
    method: "get",
});

// fetch tags (GET /tags)
export const getTags = (data = {
    promotionType: "none",
    tagCategory: "label",
    allowHigherPromotionTypes: true,
    limit: 20,
}) => request<{ data: PublicTag[], totalCount: number }>({
    data,
    endpoint: "/tags",
    method: "get",
});

// edit booking (PATCH /bookings)
export const patchBooking = (data: {
    applyToFutureInSeries?: boolean,
    autoAcceptParticipationForGroup?: AudienceGroup,
    bookingId: string,
    customMaxNumParticipants?: number,
    customMinNumParticipants?: number,
    description?: string,
    privateDescription?: string,
    detailsDescription?: string,
    durationMins?: number,
    finalizationDeadline?: string,
    newParticipants?: {
        participantRole: "standard" | "coach",
        userId: string,
    }[],
    participationRequestType?: CreateBookingParams["participationRequestType"],
    removeParticipantUserIds?: string[],
    sportForm?: string,
    standardParticipationPriceCents?: number,
    startDate?: string,
    bookingTags?: string[],
    newParticipantsAudienceGroup?: {
        clubId?: string,
        clubSite?: string,
    },
    exclusiveCompanyPriceCents?: number
}) => request<Booking>({
    data,
    endpoint: "/bookings",
    method: "patch",
});

// cancel booking (DELETE /bookings)
export const deleteBooking = (data: {
    bookingId: string,
    cancellationReason?: string,
    expectedPriceCents: number,
    forceCancel?: boolean,
    groupId?: string,
}) => request<Booking[]>({
    data,
    endpoint: "/bookings",
    method: "delete",
});

// fetch whether decline will cancel (GET /bookings/decline-will-cancel)
export const getDeclineWillCancel = (data: {
    bookingId: string,
}) => request<{ declineWillCancelBooking: boolean }>({
    data,
    endpoint: "/bookings/decline-will-cancel",
    method: "get",
});

// answer participation request
export const patchParticipationRequest = (data: {
    accept: boolean,
    bookingParticipantId: string,
}) => request<Booking>({
    data,
    endpoint: "/bookings/participation-requests",
    method: "patch",
});

// answer booking invitation
export const patchInvitation = (data: {
    accept: boolean,
    bookingId: string,
    clientLatitude?: number,
    clientLongitude?: number,
    expectedPriceCents?: number,
}) => request<Booking>({
    data,
    endpoint: "/invitations",
    method: "patch",
});

// fetch invitation payment information
export const getInvitationPayment = (data: {
    bookingId: string,
}) => request<{ priceCents: number }>({
    data,
    endpoint: "/invitations/payment",
    method: "get",
});

// fetch invitation payment information batched
export const getInvitationPaymentMultiple = (data: {
    bookingIds: string[],
}) => request<{ [id: string]: { priceCents: number } }>({
    data,
    endpoint: "/bookings/payment-info/multiple",
    method: "get",
});

// perform check-in
export const patchCheckIn = (data: {
    bookingId: string,
    clientLatitude: number,
    clientLongitude: number,
}) => request<Booking>({
    data,
    endpoint: "/bookings/check-in",
    method: "patch",
});

// request participation
export const postParticipationRequest = (data: {
    bookingId: string,
    expectedPriceCents: number,
}) => request<Booking>({
    data,
    endpoint: "/bookings/participation-requests",
    method: "post",
});

// cancel participation request
export const deleteParticipationRequest = (data: {
    bookingParticipantId: string,
}) => request<Booking>({
    data,
    endpoint: "/bookings/participation-requests",
    method: "delete",
});

// fetch participation payment information
export const getParticipationCost = (data: {
    bookingId: string,
}) => request<{ priceCents: number }>({
    data,
    endpoint: "/bookings/participation-cost",
    method: "get",
});

// fetch funds recipients choices
export const patchFundsRecipients = (data: {
    bookingId?: string,
    courtId: string,
    hideCreator?: boolean,
    participants: { userId: string, participantRole: "standard" | "coach" }[],
}) => request<BookingFundsRecipients>({
    data,
    endpoint: "/bookings/funds-recipients",
    method: "patch",
});

// fetch company clubs
export const getCompanyClubs = (data: {
    capabilities: string[],
    query?: string,
}) => request<{ data: Club[], totalCount: number }>({
    data,
    endpoint: "/root/clubs",
    method: "get",
});

// fetch company bookins
export const fetchCompanyBookings = (data: FetchBookingsParams & {
    limit?: number,
    offset?: number,
}) => request<Paginated<Booking>>({
    data,
    endpoint: "/bookings/company",
    method: "get",
});
